import React, { useState, useCallback } from 'react';
import { ChevronDown, Type, Palette, Square, Move, Maximize2, Layout, MousePointer } from 'lucide-react';
import { Slider } from './ui/slider';

// --- Helpers ---

function rgbaToHex(rgba) {
  if (!rgba) return '#000000';
  if (rgba.startsWith('#')) return rgba;
  const match = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!match) return '#000000';
  return '#' + [match[1], match[2], match[3]].map(n => parseInt(n).toString(16).padStart(2, '0')).join('');
}

function parseValue(str) {
  if (!str || str === 'auto' || str === 'none' || str === 'normal') return { value: str, unit: '' };
  const match = String(str).match(/^(-?[\d.]+)\s*(px|%|em|rem|pt|vh|vw)?$/);
  if (match) return { value: parseFloat(match[1]), unit: match[2] || 'px' };
  return { value: str, unit: '' };
}

function formatValue(val, unit) {
  if (val === 'auto' || val === 'none' || val === 'normal' || val === '') return val;
  return `${val}${unit || 'px'}`;
}

// --- Collapsible Section ---

function Section({ title, icon: Icon, defaultOpen = true, children, hidden }) {
  // r144: sections gate themselves on element kind — an icon must not
  // offer Typography. Hooks stay above this return so the hook order is
  // stable when `hidden` flips.

  const [open, setOpen] = useState(defaultOpen);
  if (hidden) return null;
  return (
    <div className="border-b border-slate-100 dark:border-slate-800">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
      >
        <Icon className="w-3.5 h-3.5" />
        <span className="flex-1 text-left">{title}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && <div className="px-3 pb-3 space-y-2">{children}</div>}
    </div>
  );
}

// --- Property Controls ---

function ColorField({ label, value, onChange }) {
  const hex = rgbaToHex(value);
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] text-slate-500 dark:text-slate-400 w-16 flex-shrink-0">{label}</span>
      <div className="flex items-center gap-1.5 flex-1">
        <input
          type="color"
          value={hex}
          onChange={(e) => onChange(e.target.value)}
          className="w-6 h-6 rounded border border-slate-200 dark:border-slate-700 cursor-pointer p-0"
        />
        <input
          type="text"
          value={hex}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 text-[11px] px-1.5 py-1 rounded border border-slate-200 dark:border-slate-700 bg-transparent font-mono"
        />
      </div>
    </div>
  );
}

function NumberField({ label, value, unit = 'px', onChange, min, max, step = 1 }) {
  const parsed = parseValue(value);
  const numVal = typeof parsed.value === 'number' ? parsed.value : 0;
  const activeUnit = parsed.unit || unit;

  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] text-slate-500 dark:text-slate-400 w-16 flex-shrink-0">{label}</span>
      <div className="flex items-center gap-1 flex-1">
        <input
          type="number"
          value={numVal}
          min={min}
          max={max}
          step={step}
          onChange={(e) => onChange(formatValue(e.target.value, activeUnit))}
          className="w-16 text-[11px] px-1.5 py-1 rounded border border-slate-200 dark:border-slate-700 bg-transparent text-center"
        />
        <span className="text-[10px] text-slate-400">{activeUnit}</span>
      </div>
    </div>
  );
}

function SelectField({ label, value, options, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] text-slate-500 dark:text-slate-400 w-16 flex-shrink-0">{label}</span>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 text-[11px] px-1.5 py-1 rounded border border-slate-200 dark:border-slate-700 bg-transparent"
      >
        {options.map(o => (
          <option key={o.value || o} value={o.value || o}>{o.label || o}</option>
        ))}
      </select>
    </div>
  );
}

function ButtonGroupField({ label, value, options, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] text-slate-500 dark:text-slate-400 w-16 flex-shrink-0">{label}</span>
      <div className="flex gap-0.5">
        {options.map(o => (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            className={`px-2 py-1 text-[10px] rounded transition-colors ${
              value === o.value
                ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 font-medium'
                : 'bg-slate-50 dark:bg-slate-800 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'
            }`}
            title={o.label}
          >
            {o.icon || o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function SpacingField({ label, values, onChange }) {
  // values = { top, right, bottom, left }
  const sides = ['top', 'right', 'bottom', 'left'];
  const labels = ['T', 'R', 'B', 'L'];

  return (
    <div>
      <span className="text-[11px] text-slate-500 dark:text-slate-400 block mb-1">{label}</span>
      <div className="flex gap-1">
        {sides.map((side, i) => {
          const parsed = parseValue(values[side]);
          const num = typeof parsed.value === 'number' ? parsed.value : 0;
          return (
            <div key={side} className="flex-1 flex flex-col items-center gap-0.5">
              <span className="text-[9px] text-slate-400">{labels[i]}</span>
              <input
                type="number"
                value={num}
                step={1}
                onChange={(e) => onChange(side, formatValue(e.target.value, parsed.unit || 'px'))}
                className="w-full text-[11px] px-1 py-0.5 rounded border border-slate-200 dark:border-slate-700 bg-transparent text-center"
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --- Main Component ---

export default function CssPropertyPanel({ selectedElement, onPropertyChange }) {
  const handleChange = useCallback((property, value) => {
    onPropertyChange(property, value);
  }, [onPropertyChange]);

  if (!selectedElement) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center px-4">
        <div className="w-10 h-10 rounded-full bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center mb-3">
          <MousePointer className="w-5 h-5 text-indigo-500" />
        </div>
        <p className="text-sm font-medium text-foreground mb-1">No element selected</p>
        <p className="text-xs text-muted-foreground max-w-[200px]">
          Click any element on the slide to inspect and edit its CSS properties
        </p>
      </div>
    );
  }

  const s = selectedElement.computedStyles || {};
  // r144: element KIND drives which sections render — the user was offered
  // typography for an icon.
  const tagK = (selectedElement.tag || '').toLowerCase();
  const isImage = ['img', 'picture', 'video'].includes(tagK);
  // <i> is a vector ONLY when empty (icon fonts) — italic prose keeps
  // its Typography controls.
  const isVector = ['svg', 'path', 'circle', 'rect', 'line', 'polyline', 'polygon', 'ellipse', 'g', 'use'].includes(tagK)
    || (tagK === 'i' && !(selectedElement.textContent || '').trim());
  const showTypography = !isImage && !isVector && !!(selectedElement.textContent || '').trim();

  return (
    <div className="flex flex-col h-full">
      {/* Element breadcrumb */}
      <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30">
        <div className="text-[11px] font-mono text-indigo-600 dark:text-indigo-400 truncate">
          &lt;{selectedElement.tag}&gt;
          {selectedElement.classList?.length > 0 && (
            <span className="text-slate-400 ml-1">.{selectedElement.classList.join('.')}</span>
          )}
        </div>
        {selectedElement.textContent && (
          <div className="text-[10px] text-slate-400 truncate mt-0.5">
            "{selectedElement.textContent}"
          </div>
        )}
      </div>

      {/* Scrollable property sections */}
      <div className="flex-1 overflow-y-auto">
        {/* r144: icons get color/fill instead of prose controls */}
        {isVector && (
          <Section title="Icon" icon={Palette}>
            <ColorField label="Color" value={s['color']} onChange={(v) => handleChange('color', v)} />
            <ColorField label="Fill" value={s['fill']} onChange={(v) => handleChange('fill', v)} />
            {/* r144b P1-5: NumberField suffixes px — 'opacity: 0.5px' is
                rejected by the browser. A unitless select instead. */}
            <SelectField label="Opacity" value={s['opacity']} options={['1', '0.9', '0.75', '0.5', '0.25', '0.1']} onChange={(v) => handleChange('opacity', v)} />
          </Section>
        )}
        {/* Typography — only for elements that actually carry text */}
        <Section title="Typography" icon={Type} hidden={!showTypography}>
          {/* r126: font family moved here from the editor's floating toolbar
              (now retired). The list is the fonts ACTUALLY LOADED in this
              slide's document (threaded through the selection payload as
              availableFonts) — offering the world's fonts would render
              fallbacks and lie about the result. The element's current face
              is always included even if it loaded from elsewhere. */}
          {(() => {
            const cur = (s['font-family'] || '').split(',')[0].trim().replace(/['"]/g, '');
            const loaded = Array.isArray(selectedElement?.availableFonts)
              ? selectedElement.availableFonts : [];
            const opts = (cur && !loaded.includes(cur)) ? [cur, ...loaded] : loaded;
            if (!opts.length) return null;
            return (
              <SelectField
                label="Font"
                value={cur}
                options={opts.map((f) => ({ value: f, label: f }))}
                onChange={(v) => handleChange('font-family', `'${v}'`)}
              />
            );
          })()}
          <NumberField
            label="Size"
            value={s['font-size']}
            onChange={(v) => handleChange('font-size', v)}
          />
          <SelectField
            label="Weight"
            value={s['font-weight']}
            options={[
              { value: '100', label: 'Thin' },
              { value: '200', label: 'Extra Light' },
              { value: '300', label: 'Light' },
              { value: '400', label: 'Normal' },
              { value: '500', label: 'Medium' },
              { value: '600', label: 'Semi Bold' },
              { value: '700', label: 'Bold' },
              { value: '800', label: 'Extra Bold' },
              { value: '900', label: 'Black' },
            ]}
            onChange={(v) => handleChange('font-weight', v)}
          />
          {/* r126: gradient-clipped text computes to a TRANSPARENT color — a
              swatch there shows black, and applying from it destroys the
              gradient. The retired toolbar carried this guard; the panel must
              too. */}
          {!/^(transparent|rgba\(0,\s*0,\s*0,\s*0\))/.test((s['color'] || '').trim()) ? (
            <ColorField
              label="Color"
              value={s['color']}
              onChange={(v) => handleChange('color', v)}
            />
          ) : (
            <div className="text-[10px] text-slate-400 dark:text-slate-500 pl-16">
              Gradient text — recolour via the gradient, not a flat colour.
            </div>
          )}
          <ButtonGroupField
            label="Align"
            value={s['text-align']}
            options={[
              { value: 'left', label: 'Left', icon: '⇤' },
              { value: 'center', label: 'Center', icon: '⇔' },
              { value: 'right', label: 'Right', icon: '⇥' },
            ]}
            onChange={(v) => handleChange('text-align', v)}
          />
          <NumberField
            label="Line H."
            value={s['line-height']}
            step={0.1}
            onChange={(v) => handleChange('line-height', v)}
          />
          <NumberField
            label="Spacing"
            value={s['letter-spacing']}
            step={0.5}
            onChange={(v) => handleChange('letter-spacing', v)}
          />
        </Section>

        {/* Background — moot for images and vector marks */}
        <Section title="Background" icon={Palette} hidden={isImage || isVector}>
          <ColorField
            label="Color"
            value={s['background-color']}
            onChange={(v) => handleChange('background-color', v)}
          />
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-slate-500 dark:text-slate-400 w-16 flex-shrink-0">Opacity</span>
            <div className="flex-1 flex items-center gap-2">
              <Slider
                value={[parseFloat(s['opacity'] || 1) * 100]}
                min={0}
                max={100}
                step={1}
                onValueChange={([v]) => handleChange('opacity', String(v / 100))}
                className="flex-1"
              />
              <span className="text-[10px] text-slate-400 w-8 text-right">
                {Math.round(parseFloat(s['opacity'] || 1) * 100)}%
              </span>
            </div>
          </div>
        </Section>

        {/* Border */}
        <Section title="Border" icon={Square}>
          <NumberField
            label="Width"
            value={s['border-top-width']}
            onChange={(v) => handleChange('border-width', v)}
          />
          <SelectField
            label="Style"
            value={s['border-top-style']}
            options={['none', 'solid', 'dashed', 'dotted', 'double']}
            onChange={(v) => handleChange('border-style', v)}
          />
          <ColorField
            label="Color"
            value={s['border-top-color']}
            onChange={(v) => handleChange('border-color', v)}
          />
          <NumberField
            label="Radius"
            value={s['border-radius']}
            onChange={(v) => handleChange('border-radius', v)}
          />
        </Section>

        {/* Spacing */}
        <Section title="Spacing" icon={Move} defaultOpen={false}>
          <SpacingField
            label="Padding"
            values={{
              top: s['padding-top'],
              right: s['padding-right'],
              bottom: s['padding-bottom'],
              left: s['padding-left'],
            }}
            onChange={(side, v) => handleChange(`padding-${side}`, v)}
          />
          <SpacingField
            label="Margin"
            values={{
              top: s['margin-top'],
              right: s['margin-right'],
              bottom: s['margin-bottom'],
              left: s['margin-left'],
            }}
            onChange={(side, v) => handleChange(`margin-${side}`, v)}
          />
        </Section>

        {/* Size */}
        <Section key={`size-${isImage || isVector}`} title="Size" icon={Maximize2} defaultOpen={isImage || isVector}>
          <NumberField
            label="Width"
            value={s['width']}
            onChange={(v) => handleChange('width', v)}
          />
          <NumberField
            label="Height"
            value={s['height']}
            onChange={(v) => handleChange('height', v)}
          />
          {isImage && (
            <SelectField
              label="Fit"
              value={s['object-fit']}
              options={['cover', 'contain', 'fill', 'none', 'scale-down']}
              onChange={(v) => handleChange('object-fit', v)}
            />
          )}
        </Section>

        {/* Layout (only shown for flex/grid containers) */}
        {(s['display'] === 'flex' || s['display'] === 'inline-flex' || s['display'] === 'grid') && (
          <Section title="Layout" icon={Layout} defaultOpen={false}>
            <SelectField
              label="Display"
              value={s['display']}
              options={['block', 'flex', 'inline-flex', 'grid', 'inline', 'inline-block', 'none']}
              onChange={(v) => handleChange('display', v)}
            />
            <ButtonGroupField
              label="Direction"
              value={s['flex-direction']}
              options={[
                { value: 'row', label: 'Row', icon: '→' },
                { value: 'column', label: 'Column', icon: '↓' },
                { value: 'row-reverse', label: 'Row Rev', icon: '←' },
                { value: 'column-reverse', label: 'Col Rev', icon: '↑' },
              ]}
              onChange={(v) => handleChange('flex-direction', v)}
            />
            <SelectField
              label="Align"
              value={s['align-items']}
              options={['flex-start', 'center', 'flex-end', 'stretch', 'baseline']}
              onChange={(v) => handleChange('align-items', v)}
            />
            <SelectField
              label="Justify"
              value={s['justify-content']}
              options={['flex-start', 'center', 'flex-end', 'space-between', 'space-around', 'space-evenly']}
              onChange={(v) => handleChange('justify-content', v)}
            />
            <NumberField
              label="Gap"
              value={s['gap']}
              onChange={(v) => handleChange('gap', v)}
            />
          </Section>
        )}
      </div>
    </div>
  );
}
